import { normalizeSiteLanguage } from '@yoyoclub/shared';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LANGUAGE_FULL_NAMES, LANGUAGE_NAMES, type Language } from '../i18n/config';

interface LanguageSwitcherProps {
  className?: string;
  /**
   * 'light' sits on a green header, 'dark' on the white backend header, 'pill' on the
   * redesigned warm-white site header (prototype .langtog).
   */
  variant?: 'light' | 'dark' | 'pill';
}

const ORDER = Object.keys(LANGUAGE_NAMES) as Language[];

/**
 * Language dropdown. The choice is also the user's language preference
 * (docs/prd-bilingual-content.md §5.6).
 */
export function LanguageSwitcher({ className = '', variant = 'dark' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const current = normalizeSiteLanguage(i18n.language) as Language;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (lang: Language) => {
    setOpen(false);
    buttonRef.current?.focus();
    if (lang !== current) void i18n.changeLanguage(lang);
  };

  const trigger =
    variant === 'pill'
      ? 'h-[34px] shrink-0 gap-1 rounded-full border-[1.5px] border-line px-2 font-ui desk:gap-1.5 desk:px-3 text-[12.5px] font-semibold text-ink-2 hover:border-primary hover:text-primary focus-visible:ring-primary/40'
      : variant === 'light'
        ? 'h-10 rounded-lg px-2.5 text-sm font-medium text-white hover:bg-white/10 focus-visible:ring-white/70'
        : 'h-10 rounded-lg px-2.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 focus-visible:ring-primary/40';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('language.switch')}: ${LANGUAGE_FULL_NAMES[current]}`}
        className={`flex items-center ${variant === 'pill' ? '' : 'gap-1.5'} transition-colors focus:outline-none focus-visible:ring-2 ${trigger}`}
      >
        {/* The phone header is tight: the pill drops the globe there. */}
        <Globe
          className={`h-4 w-4 ${variant === 'pill' ? 'hidden desk:block' : ''}`}
          aria-hidden="true"
        />
        <span className="whitespace-nowrap">{LANGUAGE_NAMES[current]}</span>
        <ChevronDown
          className={`${variant === 'pill' ? 'h-3.5 w-3.5 desk:h-4 desk:w-4' : 'h-4 w-4'} transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-40 rounded-[14px] bg-white p-1.5 shadow-lg ring-1 ring-black/5"
        >
          {ORDER.map((lang) => {
            const active = lang === current;
            return (
              <button
                key={lang}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                lang={lang}
                onClick={() => choose(lang)}
                className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  active ? 'font-semibold text-primary' : 'text-gray-800'
                }`}
              >
                {LANGUAGE_FULL_NAMES[lang]}
                {active && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
